/** Alcance estimado — solicitud comercial de licencia (usuarios / clientes). */

export const DEFAULT_ESTIMATED_USERS = 2;
export const DEFAULT_ESTIMATED_CLIENTS = 25;
export const DEFAULT_ESTIMATED_USERS_RANGE = "2";
export const DEFAULT_ESTIMATED_CLIENTS_RANGE = "11-25";

export const COMMERCIAL_REVIEW_USERS_THRESHOLD = 5;
export const SPECIAL_QUOTE_CLIENTS_THRESHOLD = 99;

export const COMMERCIAL_SCOPE_INFO_NOTE =
  "Los valores predeterminados consideran un alcance inicial de 2 usuarios y hasta 25 clientes/empresas. Si necesitas más usuarios, auxiliares o empresas, indícalo aquí; el costo puede variar según el alcance solicitado y BNT te contactará para confirmar la mejor opción.";

export const LICENSE_SCOPE_COST_DISCLAIMER =
  "El costo final puede variar según usuarios y clientes/empresas autorizados por BNT.";

export const USERS_SCOPE_OPTIONS = [
  { id: "1", label: "1 usuario: solo titular", value: 1 },
  { id: "2", label: "2 usuarios: titular + auxiliar", value: 2 },
  { id: "3-5", label: "3 a 5 usuarios", value: 5 },
  { id: "6+", label: "Más de 5 usuarios", value: 6, requiresCustom: true },
  { id: "other", label: "Otro: especificar", value: 0, requiresCustom: true },
];

export const CLIENTS_SCOPE_OPTIONS = [
  { id: "1-10", label: "Hasta 10 clientes", value: 10 },
  { id: "11-25", label: "11 a 25 clientes", value: 25 },
  { id: "26-50", label: "26 a 50 clientes", value: 50 },
  { id: "51-99", label: "51 a 99 clientes", value: 99 },
  { id: "100+", label: "Más de 99 clientes", value: 100, requiresCustom: true },
  { id: "other", label: "Otro: especificar", value: 0, requiresCustom: true },
];

export const USERS_SCOPE_HELP =
  "Indica cuántas personas usarán el sistema. Si necesitas más auxiliares, BNT revisará el alcance y te contactará.";

export const CLIENTS_SCOPE_HELP =
  "Indica aproximadamente cuántos clientes o empresas administrarás. Si el alcance supera el plan estándar, BNT te contactará para confirmar la mejor opción.";

export const LICENSE_SCOPE_MSG_USERS_REQUIRED = "Indique al menos 1 usuario estimado.";
export const LICENSE_SCOPE_MSG_CLIENTS_REQUIRED = "Indique al menos 1 cliente o empresa estimada.";

export function resolveEstimatedUsers(form = {}) {
  const range = String(form.estimated_users_range || DEFAULT_ESTIMATED_USERS_RANGE).trim();
  const raw = Math.max(0, Number(form.estimated_users) || 0);
  if (range === "other") return raw;
  if (range === "6+") return raw >= 6 ? raw : 6;
  const opt = USERS_SCOPE_OPTIONS.find((o) => o.id === range);
  if (opt) return opt.value;
  return raw || DEFAULT_ESTIMATED_USERS;
}

export function resolveEstimatedClients(form = {}) {
  const range = String(form.estimated_clients_range || DEFAULT_ESTIMATED_CLIENTS_RANGE).trim();
  const raw = Math.max(0, Number(form.estimated_clients) || 0);
  if (range === "other") return raw;
  if (range === "100+") return raw >= 100 ? raw : 100;
  const opt = CLIENTS_SCOPE_OPTIONS.find((o) => o.id === range);
  if (opt) return opt.value;
  return raw || DEFAULT_ESTIMATED_CLIENTS;
}

export function computeScopeFlags(estimatedUsers, estimatedClients) {
  const users = Math.max(0, Number(estimatedUsers) || 0);
  const clients = Math.max(0, Number(estimatedClients) || 0);
  return {
    commercial_review_required: users > COMMERCIAL_REVIEW_USERS_THRESHOLD,
    special_quote_required: clients > SPECIAL_QUOTE_CLIENTS_THRESHOLD,
  };
}

export function normalizeScopeFormFields(form = {}) {
  const estimated_users = resolveEstimatedUsers(form);
  const estimated_clients = resolveEstimatedClients(form);
  const flags = computeScopeFlags(estimated_users, estimated_clients);
  return {
    estimated_users,
    estimated_clients,
    estimated_users_range: String(form.estimated_users_range || DEFAULT_ESTIMATED_USERS_RANGE).trim(),
    estimated_clients_range: String(form.estimated_clients_range || DEFAULT_ESTIMATED_CLIENTS_RANGE).trim(),
    ...flags,
  };
}

export function validateScopeFields(form = {}) {
  const users = resolveEstimatedUsers(form);
  const clients = resolveEstimatedClients(form);
  if (users < 1) return LICENSE_SCOPE_MSG_USERS_REQUIRED;
  if (clients < 1) return LICENSE_SCOPE_MSG_CLIENTS_REQUIRED;
  const rangeU = String(form.estimated_users_range || "").trim();
  const rangeC = String(form.estimated_clients_range || "").trim();
  if ((rangeU === "6+" || rangeU === "other") && (Number(form.estimated_users) || 0) < 1) {
    return LICENSE_SCOPE_MSG_USERS_REQUIRED;
  }
  if ((rangeC === "100+" || rangeC === "other") && (Number(form.estimated_clients) || 0) < 1) {
    return LICENSE_SCOPE_MSG_CLIENTS_REQUIRED;
  }
  return null;
}

export function usersRangeRequiresCustom(rangeId) {
  return rangeId === "6+" || rangeId === "other";
}

export function clientsRangeRequiresCustom(rangeId) {
  return rangeId === "100+" || rangeId === "other";
}

export function scopeRangeLabelUsers(rangeId) {
  return USERS_SCOPE_OPTIONS.find((o) => o.id === rangeId)?.label || rangeId || "—";
}

export function scopeRangeLabelClients(rangeId) {
  return CLIENTS_SCOPE_OPTIONS.find((o) => o.id === rangeId)?.label || rangeId || "—";
}
